import React, { useState, useEffect, useRef } from "react";
import Reveal from "./Reveal";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { EXPERIENCES } from "../mock";

const Experiences = () => {
  const [perView, setPerView] = useState(3);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w < 640) setPerView(1);
      else if (w < 1024) setPerView(2);
      else setPerView(3);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const maxIndex = Math.max(0, EXPERIENCES.length - perView);
  const clamped = Math.min(index, maxIndex);
  const slideWidth = 100 / perView;

  const prev = () => setIndex((i) => Math.max(0, i - 1));
  const next = () => setIndex((i) => Math.min(maxIndex, i + 1));

  const dots = maxIndex + 1;

  return (
    <section
      id="experiences"
      data-testid="experiences-section"
      className="bg-[#F6F1E8] py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <Reveal className="max-w-2xl">
            <p className="uppercase tracking-[0.3em] text-xs font-normal text-[#B88A3B]">
              Experiences
            </p>
            <h2 className="font-serif-lux font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-6">
              Stay, Sail &amp; Experience{" "}
              <span className="gold-text italic">Marbella</span>
            </h2>
          </Reveal>
          <Reveal className="flex items-center gap-3" delay={0.1}>
            <button
              onClick={prev}
              aria-label="Previous"
              disabled={clamped === 0}
              className="w-11 h-11 rounded-full border border-[#B88A3B]/60 flex items-center justify-center text-[#B88A3B] hover:bg-[#B88A3B] hover:text-white transition-colors duration-300 disabled:opacity-30 disabled:pointer-events-none"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={next}
              aria-label="Next"
              disabled={clamped >= maxIndex}
              className="w-11 h-11 rounded-full border border-[#B88A3B]/60 flex items-center justify-center text-[#B88A3B] hover:bg-[#B88A3B] hover:text-white transition-colors duration-300 disabled:opacity-30 disabled:pointer-events-none"
            >
              <ChevronRight size={20} />
            </button>
          </Reveal>
        </div>

        <Reveal className="mt-14 overflow-hidden" delay={0.1}>
          <div
            className="flex transition-transform duration-700 ease-out -mx-3"
            style={{ transform: `translateX(-${clamped * slideWidth}%)` }}
          >
            {EXPERIENCES.map((e, i) => (
              <div
                key={i}
                className="shrink-0 px-3"
                style={{ width: `${slideWidth}%` }}
              >
                <div className="group relative overflow-hidden border border-[#B88A3B]/20 aspect-[4/3]">
                  <img
                    alt={e.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    src={e.image}
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0F1D3D]/90 via-[#0F1D3D]/40 to-transparent pt-14 pb-4 px-5 pointer-events-none">
                    <p className="font-serif-lux italic text-lg text-[#F9F9F7] leading-snug">
                      {e.caption}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        <div className="mt-8 flex items-center justify-center gap-3">
          {Array.from({ length: dots }).map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`Go to position ${i + 1}`}
              className={`h-1.5 transition-all duration-500 ${
                i === clamped
                  ? "w-8 bg-[#B88A3B]"
                  : "w-3 bg-[#0B284A]/20 hover:bg-[#0B284A]/40"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experiences;
